import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as Fuse from 'fuse.js';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import FastForwardIcon from '@material-ui/icons/FastForward';
import { Routes } from '../App/App.routes';
import { db } from '../App/+store/firebase';
import { hasClasslinkAccess } from '../Settings/Integrations/access';

import {
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Table,
  Paper,
  Modal,
  Grid,
  SnackbarContent,
  IconButton,
  Snackbar,
  Tooltip,
  TableSortLabel,
  Select,
  Icon,
  InputBase,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Cancel';
import CloseIcon from '@material-ui/icons/Close';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import { RosterUploadInstructions } from '../common/RosterUploadInstructions';
import { LoadingCircle, TitleBackButton, Button, TablePaginationActions, makeCsv, ExportCSVButton } from '../common';
import {
  sortBy,
  getStudentAvatarUrl,
  getCurrentRosterYear,
  getSubjectBand,
} from './+store/studentRoster/studentRoster.utils';
import { STAT_CARD_CATALOG, CATALOG_BY_KEY, sanitizeStatCardKeys } from './StudentRoster.statCards';
import Student from './Student';
import StudentForm from './StudentForm';
import StudentStaarForm from './StudentStaarForm';
import { styles } from './StudentRoster.styles';

const isDemo = process.env.REACT_APP_THEME === 'demo';

function TabContainer(props) {
  return (
    <Typography component="div" style={{ padding: 8 * 3 }}>
      {props.children}
    </Typography>
  );
}

TabContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

/*const styles = theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
});*/

class StudentRoster extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    loading: PropTypes.bool.isRequired,
    loadingMsg: PropTypes.string,
    user: PropTypes.object,
    profile: PropTypes.object,
    needsInit: PropTypes.bool.isRequired,
    init: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    status: PropTypes.shape({
      uploaded: PropTypes.bool.isRequired,
      error: PropTypes.string,
      message: PropTypes.string,
    }).isRequired,
    roster: PropTypes.array,
    deletingStudentId: PropTypes.string,
    editingStudentId: PropTypes.string,
    deletingStudent: PropTypes.object,
    editingStudent: PropTypes.object,
    isAddingNewStudent: PropTypes.bool.isRequired,
    cancelDelete: PropTypes.func.isRequired,
    confirmDelete: PropTypes.func.isRequired,
    handleAddStudentRequest: PropTypes.func.isRequired,
    handleCancelEdit: PropTypes.func.isRequired,
    handleCancelAdd: PropTypes.func.isRequired,
    msg: PropTypes.object,
    handleMsgClose: PropTypes.func,
    uploadedRoster: PropTypes.bool,
    avatars: PropTypes.object,
    snapshotYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectSnapshotYear: PropTypes.func,
    getSelectedYearRoster: PropTypes.func,
    rosterCSV: PropTypes.object,
    loadingCSV: PropTypes.bool,
    handleRosterExport: PropTypes.func,
    tags: PropTypes.object,
    showAcceleratedTesters: PropTypes.bool,
    statCardKeys: PropTypes.array,
    onSaveStatCards: PropTypes.func,
    classlinkConfig: PropTypes.object,
    classlinkStatus: PropTypes.object,
    loadClasslinkConfig: PropTypes.func,
    classlinkStateChanged: PropTypes.func,
  };

  state = {
    query: '',
    searchedRoster: [],
    uploadNewRoster: false,
    page: 0,
    rowsPerPage: 50,
    order: 'asc',
    orderBy: 'studentId',
    initSearchRoster: false,
    downloaded: false,
    tabValue: 0,
    removeStudentsNotInUpload: false,
    editingStatCards: false,
    draftStatCardKeys: null,
  };

  static getDerivedStateFromProps(props, state) {
    if (props.roster && props.roster.length !== state.searchedRoster.length && !state.initSearchRoster) {
      return {
        ...state,
        //initSearchRoster: true,
        searchedRoster: props.roster,
      };
    }
    return state;
  }

  componentDidMount = () => {
    this.shouldInit();
    this.initClasslink();
  };

  componentWillUnmount = () => {
    this.unsubscribeClasslink();
  };

  // ClassLink-rostered districts get a banner and lose the "remove students not in
  // upload" checkbox for the synced year (a partial flags CSV would delete them).
  initClasslink = () => {
    const { profile, classlinkConfig, loadClasslinkConfig, classlinkStateChanged } = this.props;
    if (!hasClasslinkAccess(profile)) return;
    // Guarded so componentDidUpdate can call this on every render without
    // re-requesting the config while the first call is still in flight.
    if (classlinkConfig == null && loadClasslinkConfig && !this.classlinkConfigRequested) {
      this.classlinkConfigRequested = true;
      loadClasslinkConfig();
    }
    if (profile && profile.organizationId && classlinkStateChanged && !this.classlinkRef) {
      this.classlinkRef = db.ref(`newDatabase/organizations/${profile.organizationId}/integrations/classlink`);
      this.classlinkHandler = this.classlinkRef.on(
        'value',
        (snapshot) => classlinkStateChanged(snapshot.val() || {}),
        () => {}
      );
    }
  };

  unsubscribeClasslink = () => {
    if (this.classlinkRef && this.classlinkHandler) {
      this.classlinkRef.off('value', this.classlinkHandler);
    }
    this.classlinkRef = null;
    this.classlinkHandler = null;
  };

  isClasslinkYear = () => {
    const { profile, classlinkConfig, snapshotYear } = this.props;
    if (!hasClasslinkAccess(profile) || !classlinkConfig || !classlinkConfig.enabled) return false;
    const target = classlinkConfig.targetYear || getCurrentRosterYear();
    return Number(snapshotYear) === Number(target);
  };

  renderClasslinkBanner = () => {
    const { classes, classlinkConfig, classlinkStatus } = this.props;
    if (!this.isClasslinkYear()) return null;
    const status = classlinkStatus || {};
    const when = status.lastSuccessAt ? moment(status.lastSuccessAt).format('MMM D, h:mm A') : 'never';
    const total =
      status.counts && status.counts.studentsMapped != null ? `, ${status.counts.studentsMapped} students` : '';
    const pending = status.pendingCount ? ` ${status.pendingCount} need review.` : '';
    return (
      <div className={classes.classlinkBanner}>
        <Typography color="primary">
          Rostered from ClassLink{classlinkConfig.tenantName ? ` (${classlinkConfig.tenantName})` : ''}, last synced{' '}
          {when}
          {total}.{pending} <Link to={`/${Routes.settings}/${Routes.integrationsSettings}`}>Integrations</Link>
        </Typography>
      </div>
    );
  };

  componentDidUpdate = async (prevProps) => {
    // On a direct URL load the profile arrives after mount, so the mount-time
    // initClasslink() saw no profile and bailed. Retry once the profile is here.
    this.initClasslink();
    const { profile, uploadedRoster, init, status, rosterCSV, snapshotYear } = this.props;
    const { downloaded } = this.state;
    if (status && prevProps.status !== status) {
      this.setState({ uploadNewRoster: false });
    }
    if (uploadedRoster && uploadedRoster !== prevProps.uploadedRoster) {
      setTimeout(() => init(profile), 2500);
    } else {
      this.shouldInit();
    }
    if (rosterCSV && !downloaded) {
      const { data, columns } = rosterCSV;
      const output = await makeCsv(data, columns);
      const blob = new Blob([output], { type: 'text/csv;charset=utf-8;' });
      if (blob) {
        const date = moment();
        this.setState({ downloaded: true });
        saveAs(blob, `Exported-Roster-${snapshotYear}-${date.format('MMDDYYYY')}.csv`);
      }
    }
  };

  onSearchChange = (e) => {
    this.setState({ query: e.target.value });
  };

  onSearchClick = (e) => {
    const options = {
      shouldSort: true,
      threshold: 0,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['1.studentId', '1.firstName', '1.lastName', '1.tsdsId', '1.localId'],
    };
    const { roster } = this.props;
    const { query } = this.state;
    let searchedRoster = roster;
    const fuse = new Fuse(searchedRoster, options);
    if (query) {
      searchedRoster = fuse.search(query);
    }
    this.setState({ searchedRoster, initSearchRoster: !!query });
  };

  createSortHandler = (property) => (event) => {
    this.handleRequestSort(event, property);
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  handleRequestSort = (event, property) => {
    const { orderBy, order } = this.state;
    let newOrder = 'desc';
    if (orderBy === property && order === 'desc') {
      newOrder = 'asc';
    }
    this.setState({ order: newOrder, orderBy: property });
  };

  uploadNewRosterToggle = () => {
    const { uploadNewRoster } = this.state;
    this.setState({ uploadNewRoster: !uploadNewRoster });
  };

  onDrop = (accepted, rejected, removeStudentsNotInUpload) => {
    const { onDrop, user, profile, snapshotYear } = this.props;
    onDrop(accepted, rejected, user, profile, snapshotYear, removeStudentsNotInUpload);
  };

  renderSnackbar = () => {
    const { classes, msg, handleMsgClose } = this.props;
    const Icon = msg.isError ? ErrorIcon : CheckCircleIcon;
    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open
        onClose={handleMsgClose}
      >
        <SnackbarContent
          className={classNames(classes[msg.isError ? 'error' : 'success'], classes.margin)}
          aria-describedby="client-snackbar"
          message={
            <span id="client-snackbar" className={classes.message}>
              <Icon className={classNames(classes.icon, classes.iconVariant)} />
              {msg.text}
            </span>
          }
          action={[
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              className={classes.close}
              onClick={handleMsgClose}
            >
              <CloseIcon className={classes.icon} />
            </IconButton>,
          ]}
        />
      </Snackbar>
    );
  };

  handleConfirmDelete = () => {
    const { profile, deletingStudentId, confirmDelete, snapshotYear } = this.props;
    confirmDelete(deletingStudentId, profile.organizationId, snapshotYear);
  };

  shouldInit() {
    const { profile, needsInit, init, loading } = this.props;
    if (profile && needsInit && !loading) {
      init(profile);
    }
  }

  onSearchEnter = (e) => {
    if (e.key === 'Enter') {
      this.onSearchClick();
    }
  };

  renderSearchBar = () => {
    const { classes } = this.props;
    return (
      <Paper className={isDemo ? classes.searchWrapperDemo : classes.searchWrapper}>
        <InputBase
          onKeyDown={this.onSearchEnter}
          onChange={this.onSearchChange}
          className={classes.input}
          placeholder="Search By Name or ID"
          inputProps={{ 'aria-label': 'search by name or id' }}
        />
        <IconButton className={classes.iconButton} aria-label="search" onClick={this.onSearchClick}>
          <SearchIcon />
        </IconButton>
      </Paper>
    );
  };

  renderDeleteModal = (student) => {
    const { cancelDelete, classes } = this.props;
    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open
        onClose={cancelDelete}
      >
        <div className={classes.deleteModal}>
          <Typography variant="h6" id="modal-title" gutterBottom>
            {`Are you sure you want to delete records of
            ${student.firstName} ${student.lastName} with ID number of
            ${student.studentId || student.tsdsId || student.localId}?`}
          </Typography>
          <Grid container direction="row" justify="space-between" alignItems="center">
            <Button color="dark" onClick={this.handleConfirmDelete}>
              Yes, I am sure.
            </Button>
            <Button color="light" onClick={cancelDelete}>
              Cancel
            </Button>
          </Grid>
        </div>
      </Modal>
    );
  };

  renderEditModal = (student, tags) => {
    const { handleCancelEdit, classes, avatars } = this.props;
    const { tabValue } = this.state;
    var studentAvatar = getStudentAvatarUrl(student, avatars);
    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open
        onClose={handleCancelEdit}
      >
        <div className={classes.editModal}>
          <Typography variant="h6" id="modal-title">
            {`${student.firstName} ${student.lastName} (${student.localId || student.studentId || student.tsdsId})`}
          </Typography>

          <Tabs value={tabValue} onChange={this.handleTabChange}>
            <Tab label="Student Info" />
            <Tab label="Prior Year Staar" />
          </Tabs>
          {tabValue === 0 && (
            <TabContainer>
              <StudentForm studentAvatar={studentAvatar} tags={tags} />
            </TabContainer>
          )}
          {tabValue === 1 && (
            <TabContainer>
              {' '}
              <StudentStaarForm />
            </TabContainer>
          )}
        </div>
      </Modal>
    );
  };

  handleSnapshotYearSelect = (e) => {
    const { onSelectSnapshotYear, getSelectedYearRoster, profile } = this.props;
    const { value } = e.target;
    onSelectSnapshotYear(value);
    getSelectedYearRoster(profile, value);
    this.setState({ initSearchRoster: false, query: '' });
  };

  renderSnapshotYearSelector = () => {
    const { snapshotYear } = this.props;
    const currentYear = getCurrentRosterYear();
    const options = [
      { value: currentYear, label: `${currentYear}-${currentYear + 1}` },
      { value: currentYear - 1, label: `${currentYear - 1}-${currentYear}` },
    ];
    return (
      <Grid item>
        <Typography color="primary">Snapshot Year</Typography>
        <Select onChange={this.handleSnapshotYearSelect} value={snapshotYear}>
          {options.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    );
  };

  handleTabChange = (event, value) => {
    this.setState({ tabValue: value });
  };

  renderAddModal = (tags) => {
    const { classes, handleCancelAdd } = this.props;
    const { tabValue } = this.state;
    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open
        onClose={handleCancelAdd}
      >
        <div className={classes.editModal}>
          <Typography variant="h6" id="modal-title">
            Add New Student
          </Typography>
          <Tabs value={tabValue} onChange={this.handleTabChange}>
            <Tab label="Student Info" />
          </Tabs>
          {tabValue === 0 && (
            <TabContainer>
              <StudentForm tags={tags} />
            </TabContainer>
          )}
        </div>
      </Modal>
    );
  };

  // The effective (saved) set of cards, sanitized against the catalog. A never-saved preference
  // (null) falls back to the catalog defaults; an explicit empty list is honored.
  getActiveStatCardKeys = () => sanitizeStatCardKeys(this.props.statCardKeys);

  toggleEditStatCards = () => {
    this.setState((prev) =>
      prev.editingStatCards
        ? { editingStatCards: false, draftStatCardKeys: null }
        : { editingStatCards: true, draftStatCardKeys: this.getActiveStatCardKeys() }
    );
  };

  cancelEditStatCards = () => {
    this.setState({ editingStatCards: false, draftStatCardKeys: null });
  };

  removeStatCard = (key) => {
    this.setState((prev) => ({ draftStatCardKeys: (prev.draftStatCardKeys || []).filter((k) => k !== key) }));
  };

  addStatCard = (key) => {
    this.setState((prev) => ({ draftStatCardKeys: [...(prev.draftStatCardKeys || []), key] }));
  };

  saveStatCards = () => {
    const { user, profile, onSaveStatCards } = this.props;
    const uid = (user && user.uid) || (profile && profile.uid) || null;
    const keys = sanitizeStatCardKeys(this.state.draftStatCardKeys);
    // Always dispatch so the reducer applies the choice this session; the epic persists it
    // remotely when a uid is available.
    if (onSaveStatCards) {
      onSaveStatCards(uid, keys);
    }
    this.setState({ editingStatCards: false, draftStatCardKeys: null });
  };

  renderStatCards = () => {
    const { classes, roster } = this.props;
    if (!isDemo || !roster || !roster.length) return null;
    const { editingStatCards, draftStatCardKeys } = this.state;
    // Each card knows how to count itself from the roster (see StudentRoster.statCards). While
    // editing, render from the in-progress draft; otherwise from the saved/effective selection.
    const activeKeys = editingStatCards ? sanitizeStatCardKeys(draftStatCardKeys) : this.getActiveStatCardKeys();
    const hiddenCards = STAT_CARD_CATALOG.filter((card) => !activeKeys.includes(card.key));
    return (
      <div className={classes.statCardsBlock}>
        <div className={classes.statCardsToolbar}>
          {editingStatCards ? (
            <React.Fragment>
              <span className={classes.statCardsHint}>Click a card&apos;s ✕ to remove, or a + to add one</span>
              <Button color="dark" noMargin onClick={this.saveStatCards}>
                Done
              </Button>
              <Button color="transparent" noMargin onClick={this.cancelEditStatCards}>
                Cancel
              </Button>
            </React.Fragment>
          ) : (
            <Tooltip title="Customize cards">
              <IconButton className={classes.statCardsEditBtn} onClick={this.toggleEditStatCards}>
                <Icon style={{ fontSize: 18 }}>edit</Icon>
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className={classes.statCardRow}>
          {activeKeys.map((key) => {
            const card = CATALOG_BY_KEY[key];
            if (!card) return null;
            return (
              <div className={classes.statCard} key={key}>
                {editingStatCards && (
                  <IconButton
                    className={classes.statCardRemove}
                    onClick={() => this.removeStatCard(key)}
                    aria-label={`Remove ${card.label}`}
                  >
                    <Icon style={{ fontSize: 16 }}>close</Icon>
                  </IconButton>
                )}
                <div className={classes.statNumber} style={card.color ? { color: card.color } : undefined}>
                  {card.compute(roster).toLocaleString()}
                </div>
                <div className={classes.statLabel}>{card.label}</div>
              </div>
            );
          })}
          {editingStatCards &&
            hiddenCards.map((card) => (
              <div
                className={classes.statCardAdd}
                key={card.key}
                role="button"
                tabIndex={0}
                onClick={() => this.addStatCard(card.key)}
              >
                <div className={classes.statCardAddPlus}>+</div>
                <div className={classes.statCardAddLabel}>{card.label}</div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  renderStudentRosterTable = () => {
    const { classes, avatars } = this.props;
    const { rowsPerPage, page, order, orderBy, searchedRoster } = this.state;

    const rosterTableHeaders = {
      name: 'Name',
      sexCode: 'Sex',
      dateOfBirth: 'Date of Birth',
      studentId: 'STUDENT-ID',
      tsdsId: 'TSDS Unique ID',
      localId: 'Local ID',
      gradeLevel: 'Grade Level',
      gradYear: 'Graduation Date',
      campusNumber: 'Campus Number',
      ethnicity: 'Ethnicity',
      ecoDis: 'EcoDis',
      spEd: 'Special Education',
      lep: 'English Learner',
      enrollment: 'Enrollment',
      snapshot: 'Snapshot',
      yearsInUS: 'Years in US Schools',
      asylumStatus: 'Asylee / Refugee / SIFE',
      highlyMobile: 'Highly Mobile',
      monitor: 'Monitor',
      mentor: 'Mentor',
      acceleratedTesterMath: 'Accelerated Tester Math',
      acceleratedTesterReading: 'Accelerated Tester Reading',
      acceleratedTesterScience: 'Accelerated Tester Science',
    };

    const tableHeaders = Object.entries(rosterTableHeaders);
    const table = (
      <Paper className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow className={classes.tableRow}>
              <TableCell key="op" align="center" className={classes.iconHead}>
                Options
              </TableCell>
              <TableCell style={{ width: 60 }} align="center" className={classes.iconHead}>
                Profile Picture
              </TableCell>
              {tableHeaders.map(([id, header], index) => {
                // Accelerated Tester column headers carry their subject's report-band color.
                const headerSubject =
                  id === 'acceleratedTesterMath'
                    ? 'math'
                    : id === 'acceleratedTesterReading'
                    ? 'reading'
                    : id === 'acceleratedTesterScience'
                    ? 'science'
                    : null;
                const band = getSubjectBand(headerSubject);
                return (
                  <TableCell
                    key={id}
                    align="center"
                    className={classes.head}
                    sortDirection={orderBy === id ? order : false}
                    style={band ? { background: band } : undefined}
                  >
                    <Tooltip
                      title={id === 'name' ? 'Sort By Last Name' : 'Sort'}
                      placement="bottom-end"
                      enterDelay={300}
                    >
                      <TableSortLabel
                        active={orderBy === id}
                        direction={order}
                        onClick={this.createSortHandler(id)}
                        style={{
                          whiteSpace: index === 0 ? 'nowrap' : '',
                          width: 1550 / tableHeaders.length,
                        }}
                      >
                        <div style={{ width: 16, margin: 4 }} />
                        {header}
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                );
              })}
              <TableCell
                key="testsHeader"
                align="center"
                className={classes.head}
                style={{
                  width: 150,
                }}
              >
                Tests
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {sortBy(searchedRoster, order, orderBy)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(([firebaseId, student]) => {
                const studentAvatar = getStudentAvatarUrl(student, avatars);
                return (
                  <Student
                    rosterTableHeaders={rosterTableHeaders}
                    key={firebaseId}
                    firebaseId={firebaseId}
                    student={student}
                    studentAvatar={studentAvatar}
                  />
                );
              })}
          </TableBody>
          <TableFooter className={classes.pagination}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                colSpan={3}
                count={searchedRoster.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  native: true,
                }}
                onChangePage={this.handleChangePage}
                onChangeRowsPerPage={this.handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    );
    if (!isDemo) return table;
    // Demo: frame the table in a soft panel and move the search box into the header bar.
    return (
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <span className={classes.panelTitle}>Roster</span>
          {this.renderSearchBar()}
        </div>
        {table}
      </div>
    );
  };

  exportCsv = () => {
    const { handleRosterExport, snapshotYear } = this.props;
    this.setState({ downloaded: false });
    handleRosterExport(snapshotYear);
  };

  renderAcceleratedTestersShortcut = () => {
    const { showAcceleratedTesters } = this.props;
    if (!showAcceleratedTesters) return null;
    // Accelerated-tester status is per-student roster info (it even shows as columns
    // in the table below), so we surface its import/review page right here. Reuses the
    // existing /accelerated-testers route — no full-page reload (react-router Link).
    return (
      <Link to={`/${Routes.acceleratedTesters}`} style={{ textDecoration: 'none' }}>
        <Button color="outlined" large>
          <FastForwardIcon style={{ fontSize: 18, marginRight: 6 }} />
          Accelerated Testers
        </Button>
      </Link>
    );
  };

  renderActionButtons = () => {
    const { roster, handleAddStudentRequest, loadingCSV, classes } = this.props;
    const { uploadNewRoster } = this.state;
    const isEmptyRoster = !roster || !roster.length;
    if (!isEmptyRoster) {
      if (uploadNewRoster) {
        return (
          <Grid container direction="row" justify="space-between" alignItems="center">
            <Grid item>
              <Button color="outlined" onClick={this.uploadNewRosterToggle}>
                Go back to roster list
              </Button>
            </Grid>
            {this.renderSnapshotYearSelector()}
          </Grid>
        );
      }
      return (
        <Grid container direction="row" justify="space-between" alignItems="center">
          {this.renderSnapshotYearSelector()}
          {/* In demo the search box lives in the panel header bar, so it's dropped here. */}
          {!isDemo && this.renderSearchBar()}
          <Grid item className={isDemo ? classes.actionButtonGroupDemo : undefined}>
            {this.renderAcceleratedTestersShortcut()}
            <Button color="outlined" large onClick={this.uploadNewRosterToggle}>
              Upload a New Roster
            </Button>
            <Button color="dark" large onClick={handleAddStudentRequest}>
              Add new student
            </Button>
            <ExportCSVButton title="Export CSV" onClick={this.exportCsv} loading={loadingCSV} />
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container direction="row" justify="space-between" alignItems="center">
        {this.renderSnapshotYearSelector()}
      </Grid>
    );
  };

  render() {
    const {
      profile,
      user,
      status,
      classes,
      roster,
      loading,
      loadingMsg,
      deletingStudent,
      editingStudent,
      deletingStudentId,
      editingStudentId,
      isAddingNewStudent,
      msg,
      uploadedRoster,
      avatars,
      snapshotYear,
      tags,
    } = this.props;

    const { uploadNewRoster } = this.state;
    const isEmptyRoster = !roster || !roster.length;
    if (isEmptyRoster && uploadedRoster) {
      return (
        <div className={classes.content}>
          <Typography
            style={{
              textAlign: 'center',
              fontSize: 16,
              marginTop: '10%',
            }}
          >
            We are currently processing your roster. Please check back in a few minutes.
          </Typography>
        </div>
      );
    }
    if (loading || !profile || (isEmptyRoster && uploadedRoster) || !avatars) {
      return (
        <div className={classes.content}>
          <LoadingCircle classes={classes} msg={loadingMsg} />
        </div>
      );
    }
    return (
      <div className={classNames(classes.root, { [classes.coloredRoot]: isEmptyRoster || uploadNewRoster })}>
        <TitleBackButton title="Student Roster" />
        {!!msg && this.renderSnackbar()}
        {this.renderClasslinkBanner()}
        {this.renderActionButtons()}
        {!isEmptyRoster && !uploadNewRoster && this.renderStatCards()}
        {isDemo && isEmptyRoster && !uploadNewRoster && (
          <div className={classes.emptyState}>
            <Icon className={classes.emptyStateIcon}>group</Icon>
            <div className={classes.emptyStateHeadline}>No students yet</div>
            <div className={classes.emptyStateSubtext}>Upload a roster file below to get started.</div>
          </div>
        )}
        {!isEmptyRoster && !uploadNewRoster && this.renderStudentRosterTable()}
        {(isEmptyRoster || uploadNewRoster) && (
          <RosterUploadInstructions
            isEmptyRoster={isEmptyRoster}
            user={user}
            status={status}
            onDrop={this.onDrop}
            onUploadClick={() => {}}
            snapshotYear={snapshotYear}
            profile={profile}
            classlinkManaged={this.isClasslinkYear()}
          />
        )}
        {!!deletingStudentId && !isEmptyRoster && this.renderDeleteModal(deletingStudent)}
        {!!editingStudentId && !isEmptyRoster && this.renderEditModal(editingStudent, tags)}
        {!!isAddingNewStudent && !isEmptyRoster && this.renderAddModal(tags)}
      </div>
    );
  }
}

export default withStyles(styles)(StudentRoster);
